import React, { useState, useMemo } from 'react';
import { NetworkRequest } from '../utils/types';
import { T } from '../designTokens';

interface NetworkTraceProps {
  requests?: NetworkRequest[];
  title?: string;
  initialFilter?: string;
}

export default function NetworkTrace({ requests = [], title = 'Network Trace', initialFilter = '' }: NetworkTraceProps) {
  const [searchTerm, setSearchTerm] = useState(initialFilter);
  const [expanded, setExpanded] = useState(false);
  const [filterThirdParty, setFilterThirdParty] = useState(false);
  const [filterResourceType, setFilterResourceType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'time' | 'size' | 'duration'>('time');
  const [selectedRequest, setSelectedRequest] = useState<NetworkRequest | null>(null);

  const stats = useMemo(() => {
    const thirdPartyCount = requests.filter((r) => r.isThirdParty).length;
    const failedCount = requests.filter((r) => r.failed).length;
    const totalSize = requests.reduce((sum, r) => sum + (r.size || 0), 0);
    const avgDuration = requests.filter(r => r.duration).length > 0
      ? requests.reduce((sum, r) => sum + (r.duration || 0), 0) / requests.filter(r => r.duration).length
      : 0;
    const resourceTypes = requests.reduce((acc, r) => {
      acc[r.resourceType] = (acc[r.resourceType] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return { total: requests.length, thirdParty: thirdPartyCount, firstParty: requests.length - thirdPartyCount, failed: failedCount, totalSize, avgDuration, resourceTypes };
  }, [requests]);

  const filteredRequests = useMemo(() => {
    let filtered = [...requests];
    if (filterThirdParty) filtered = filtered.filter((r) => r.isThirdParty);
    if (filterResourceType !== 'all') filtered = filtered.filter((r) => r.resourceType === filterResourceType);
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        r.url.toLowerCase().includes(lowerSearch) || 
        r.domain.toLowerCase().includes(lowerSearch)
      );
    }
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'time': return new Date(a.requestTime).getTime() - new Date(b.requestTime).getTime();
        case 'size': return (b.size || 0) - (a.size || 0);
        case 'duration': return (b.duration || 0) - (a.duration || 0);
        default: return 0;
      }
    });
    return filtered;
  }, [requests, filterThirdParty, filterResourceType, sortBy, searchTerm]);

  if (!requests || requests.length === 0) return null;

  const formatBytes = (bytes?: number) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms.toFixed(0)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getStatusColor = (statusCode?: number, failed?: boolean) => {
    if (failed) return T.error;
    if (!statusCode) return T.gray400;
    if (statusCode >= 200 && statusCode < 300) return T.success;
    if (statusCode >= 300 && statusCode < 400) return T.warning;
    return T.error;
  };

  return (
    <div className="network-trace-container">
      {/* Header */}
      <div
        className={`network-trace-header ${expanded ? 'network-trace-header--expanded' : ''}`}
        onClick={() => setExpanded(!expanded)}
      >
        <h3 className="network-trace-title">
          {title}
        </h3>
        <div className="network-trace-header-right">
          <span className="network-trace-badge">
            {stats.total} requests
          </span>
          <span className="network-trace-toggle-arrow">{expanded ? '▼' : '▶'}</span>
        </div>
      </div>

      {expanded && (
        <>
          {/* Stats grid */}
          <div className="network-trace-stats-grid">
            {[
              { label: 'Total Requests', value: stats.total, className: '', valueColor: 'normal' },
              { label: '3rd Party', value: stats.thirdParty, className: 'network-trace-stat-card--warning', valueColor: 'warning' },
              { label: '1st Party', value: stats.firstParty, className: 'network-trace-stat-card--info', valueColor: 'info' },
              { label: 'Failed', value: stats.failed, className: 'network-trace-stat-card--error', valueColor: 'error' },
              { label: 'Total Size', value: formatBytes(stats.totalSize), className: '', valueColor: 'normal' },
              { label: 'Avg Duration', value: formatDuration(stats.avgDuration), className: '', valueColor: 'normal' },
            ].map((s) => (
              <div
                key={s.label}
                className={`network-trace-stat-card ${s.className}`}
              >
                <div className="network-trace-stat-label">{s.label}</div>
                <div className={`network-trace-stat-value network-trace-stat-value--${s.valueColor}`}>{s.value}</div>
              </div>
            ))}
            <div style={{ flex: 1, minWidth: 200 }}>
              <input 
                type="text" 
                placeholder="Search URLs or domains..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ width: '100%', padding: '6px 12px', border: `1px solid ${T.gray300}`, borderRadius: 6, fontSize: 13 }}
              />
            </div>
          </div>

          {/* Filters */}
          <div className="network-trace-filters">
            <label className="network-trace-filter-label">
              <input
                type="checkbox"
                checked={filterThirdParty}
                onChange={(e) => setFilterThirdParty(e.target.checked)}
              />
              <span>3rd party only</span>
            </label>

            <select
              value={filterResourceType}
              onChange={(e) => setFilterResourceType(e.target.value)}
              className="network-trace-select"
            >
              <option value="all">All types</option>
              {Object.keys(stats.resourceTypes).sort().map((type) => (
                <option key={type} value={type}>
                  {type} ({stats.resourceTypes[type]})
                </option>
              ))}
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'time' | 'size' | 'duration')}
              className="network-trace-select"
            >
              <option value="time">Sort by time</option>
              <option value="size">Sort by size</option>
              <option value="duration">Sort by duration</option>
            </select>
          </div>

          {/* Request table */}
          <div className="network-trace-table-wrapper">
            <table className="network-trace-table">
              <thead className="network-trace-table-header">
                <tr>
                  {['Method', 'Type', 'URL', 'Status', 'Size', 'Time'].map((h, idx) => (
                    <th
                      key={h}
                      className="network-trace-th"
                      style={{
                        textAlign: idx >= 4 ? 'right' : 'left',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((req, idx) => (
                  <tr
                    key={idx}
                    onClick={() => setSelectedRequest(req)}
                    className="network-trace-tr"
                    style={{
                      backgroundColor: selectedRequest === req ? T.cobalt50 : 'transparent',
                    }}
                  >
                    <td className="network-trace-td-mono">
                      <span
                        className={`network-trace-method-badge network-trace-method-badge--${req.method.toLowerCase() === 'get' ? 'get' : 'post'}`}
                      >
                        {req.method}
                      </span>
                    </td>
                    <td className="network-trace-td" style={{ color: T.gray600, fontSize: 13 }}>{req.resourceType}</td>
                    <td
                      className="network-trace-td-url"
                      title={req.url}
                    >
                      {req.isThirdParty && <span style={{ color: T.warning, marginRight: 4 }}>ext</span>}
                      {req.domain}
                    </td>
                    <td className="network-trace-td">
                      {req.failed ? (
                        <span style={{ color: T.error, fontWeight: 500 }}>Failed</span>
                      ) : (
                        <span style={{ color: getStatusColor(req.statusCode, req.failed), fontWeight: 500 }}>
                          {req.statusCode || 'N/A'}
                        </span>
                      )}
                    </td>
                    <td className="network-trace-td-mono" style={{ textAlign: 'right', color: T.gray500 }}>
                      {formatBytes(req.size)}
                    </td>
                    <td className="network-trace-td-mono" style={{ textAlign: 'right', color: T.gray500 }}>
                      {formatDuration(req.duration)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredRequests.length === 0 && (
              <div style={{ padding: 32, textAlign: 'center', color: T.gray400 }}>
                No requests match the current filters
              </div>
            )}
          </div>

          {/* Request detail modal */}
          {selectedRequest && (
            <div
              className="network-trace-modal-overlay"
              onClick={() => setSelectedRequest(null)}
            >
              <div
                className="network-trace-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="network-trace-modal-title">
                  Request Details
                </h3>

                <div className="network-trace-modal-field">
                  <div className="network-trace-modal-field-label">URL</div>
                  <div className="network-trace-modal-url-box">
                    {selectedRequest.url}
                  </div>
                </div>

                <div className="network-trace-modal-grid">
                  {[
                    { label: 'Method', value: selectedRequest.method },
                    { label: 'Type', value: selectedRequest.resourceType },
                    {
                      label: 'Status',
                      value: selectedRequest.failed ? `Failed: ${selectedRequest.failureReason}` : (selectedRequest.statusCode || 'N/A'),
                      color: getStatusColor(selectedRequest.statusCode, selectedRequest.failed),
                    },
                    { label: 'Party', value: selectedRequest.isThirdParty ? '3rd Party' : '1st Party' },
                    { label: 'Size', value: formatBytes(selectedRequest.size) },
                    { label: 'Duration', value: formatDuration(selectedRequest.duration) },
                  ].map((item) => (
                    <div key={item.label}>
                      <div className="network-trace-modal-field-label">{item.label}</div>
                      <div className="network-trace-modal-field-value" style={{ color: item.color }}>{item.value}</div>
                    </div>
                  ))}
                </div>

                {selectedRequest.requestHeaders && Object.keys(selectedRequest.requestHeaders).length > 0 && (
                  <div className="network-trace-modal-headers-section">
                    <div className="network-trace-modal-headers-label">Request Headers</div>
                    <div className="network-trace-modal-headers-box">
                      {Object.entries(selectedRequest.requestHeaders).map(([key, value]) => (
                        <div key={key} className="network-trace-modal-header-line">
                          <span className="network-trace-modal-header-key" style={{ color: T.cobalt600 }}>{key}:</span> {value}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedRequest.responseHeaders && Object.keys(selectedRequest.responseHeaders).length > 0 && (
                  <div className="network-trace-modal-headers-section">
                    <div className="network-trace-modal-headers-label">Response Headers</div>
                    <div className="network-trace-modal-headers-box">
                      {Object.entries(selectedRequest.responseHeaders).map(([key, value]) => (
                        <div key={key} className="network-trace-modal-header-line">
                          <span className="network-trace-modal-header-key" style={{ color: T.success }}>{key}:</span> {value}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setSelectedRequest(null)}
                  className="network-trace-modal-close-btn"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
